import type { Message, MessageStatus, TeamMember } from '../types';
import { h } from '../utils/dom';
import { escapeHtml } from '../utils/sanitize';
import { renderAgentAvatar } from './avatar';

export class MessagesView {
  el: HTMLDivElement;
  private typingEl: HTMLDivElement | null = null;
  private team: TeamMember[];
  /** Linhas renderizadas por id da mensagem (para atualizar estado de entrega). */
  private rows = new Map<number, HTMLElement>();

  constructor(team: TeamMember[], welcomeMsg: string | null, offlineMsg: string | null = null) {
    this.team = team;
    this.el = h('div', { class: 'att-messages', role: 'log', 'aria-label': 'Mensagens' }) as HTMLDivElement;

    if (welcomeMsg) {
      this.el.appendChild(h('div', { class: 'att-welcome' }, welcomeMsg));
    }
    if (offlineMsg) {
      this.el.appendChild(h('div', { class: 'att-notice att-offline' }, offlineMsg));
    }
  }

  renderMessages(messages: Message[]): void {
    // Limpa as mensagens, mantendo o cabeçalho (boas-vindas/aviso offline)
    const keep = Array.from(this.el.querySelectorAll('.att-welcome, .att-offline'));
    this.el.innerHTML = '';
    keep.forEach((node) => this.el.appendChild(node));
    this.rows.clear();
    this.typingEl = null;

    for (const msg of messages) {
      this.appendMessage(msg, false);
    }
    this.scrollToBottom();
  }

  appendMessage(msg: Message, scroll = true): void {
    const isVisitor = msg.from === 'visitor';
    const row = h('div', {
      class: `att-msg-row att-msg-row--${isVisitor ? 'visitor' : 'agent'}`,
    });

    if (!isVisitor) {
      const avatarWrap = h('div', { class: 'att-msg-avatar-wrap' });
      const agentMember = msg.name
        ? this.team.find(t => t.name === msg.name || t.name.split(/\s+/)[0] === msg.name) ?? null
        : (this.team[0] ?? null);
      renderAgentAvatar(agentMember, avatarWrap);
      row.appendChild(avatarWrap);
    }

    const msgContent = h('div', { class: 'att-msg-content' });

    const bubble = h('div', { class: `att-bubble att-bubble--${isVisitor ? 'visitor' : 'agent'}` });
    // Texto sempre escapado: nada do que vem da rede vira HTML.
    bubble.innerHTML = escapeHtml(msg.content).replace(/\n/g, '<br>');
    msgContent.appendChild(bubble);

    const meta = h('div', { class: 'att-msg-meta' });
    meta.appendChild(h('span', { class: 'att-msg-time' }, formatTime(msg.createdAt)));
    if (!isVisitor && msg.name) {
      meta.appendChild(h('span', { class: 'att-msg-name' }, msg.name));
    }
    msgContent.appendChild(meta);
    row.appendChild(msgContent);

    // Insere antes do "digitando…" se estiver visível
    if (this.typingEl) {
      this.el.insertBefore(row, this.typingEl);
    } else {
      this.el.appendChild(row);
    }
    this.rows.set(msg.id, row);

    if (isVisitor && msg.status && msg.status !== 'sent') {
      this.setMessageStatus(msg.id, msg.status);
    }
    if (scroll) this.scrollToBottom();
  }

  /** Estado de entrega de uma mensagem do visitante: enviando, enviada ou falhou (com "tentar de novo"). */
  setMessageStatus(id: number, status: MessageStatus, onRetry?: () => void, reason?: string): void {
    const row = this.rows.get(id);
    if (!row) return;

    row.classList.toggle('att-msg-row--sending', status === 'sending');
    row.classList.toggle('att-msg-row--failed', status === 'failed');

    const meta = row.querySelector('.att-msg-meta');
    meta?.querySelectorAll('.att-msg-status, .att-msg-retry').forEach((node) => node.remove());
    if (!meta || status === 'sent') return;

    if (status === 'sending') {
      meta.appendChild(h('span', { class: 'att-msg-status' }, 'Enviando…'));
      return;
    }

    meta.appendChild(h('span', { class: 'att-msg-status att-msg-status--failed', role: 'alert' }, reason ?? 'Não enviada'));
    if (onRetry) {
      const retry = h('button', { class: 'att-msg-retry', type: 'button' }, 'Tentar de novo');
      retry.addEventListener('click', onRetry);
      meta.appendChild(retry);
    }
  }

  /** A mensagem local ganhou o id definitivo do servidor (evita duplicar quando o histórico voltar). */
  replaceMessageId(oldId: number, newId: number): void {
    const row = this.rows.get(oldId);
    if (!row || oldId === newId) return;
    this.rows.delete(oldId);
    this.rows.set(newId, row);
  }

  /** Linha informativa centralizada (ex.: "Atendimento encerrado"). */
  appendNotice(text: string): void {
    const notice = h('div', { class: 'att-notice', role: 'status' }, text);
    if (this.typingEl) {
      this.el.insertBefore(notice, this.typingEl);
    } else {
      this.el.appendChild(notice);
    }
    this.scrollToBottom();
  }

  showTyping(): void {
    if (this.typingEl) return;
    this.typingEl = h('div', { class: 'att-typing' }) as HTMLDivElement;

    const avatarWrap = h('div', { class: 'att-msg-avatar-wrap' });
    renderAgentAvatar(this.team[0] ?? null, avatarWrap);
    this.typingEl.appendChild(avatarWrap);

    const dots = h('div', { class: 'att-typing-dots' });
    dots.appendChild(h('span', { class: 'att-typing-dot' }));
    dots.appendChild(h('span', { class: 'att-typing-dot' }));
    dots.appendChild(h('span', { class: 'att-typing-dot' }));
    this.typingEl.appendChild(dots);

    this.el.appendChild(this.typingEl);
    this.scrollToBottom();
  }

  hideTyping(): void {
    if (this.typingEl) {
      this.typingEl.remove();
      this.typingEl = null;
    }
  }

  showError(onRetry: () => void): void {
    const errEl = h('div', { class: 'att-error-state' });
    // [B4-14] innerHTML seguro: conteúdo 100% estático (SVG literal, sem dados externos).
    errEl.innerHTML = `
      <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
        <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><circle cx="12" cy="16" r="0.5" fill="currentColor"/>
      </svg>
    `;
    errEl.appendChild(h('p', {}, 'Não foi possível conectar. Verifique sua conexão.'));
    const retryBtn = h('button', { class: 'att-retry-btn', type: 'button' }, 'Tentar novamente');
    retryBtn.addEventListener('click', onRetry);
    errEl.appendChild(retryBtn);
    this.el.innerHTML = '';
    this.rows.clear();
    this.typingEl = null;
    this.el.appendChild(errEl);
  }

  scrollToBottom(): void {
    this.el.scrollTop = this.el.scrollHeight;
  }
}

function formatTime(isoDate: string): string {
  try {
    const d = new Date(isoDate);
    if (isNaN(d.getTime())) return '';
    return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  } catch {
    return '';
  }
}
